import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Loader2, Check, Zap, Plus, Trash2, ChevronDown, ChevronRight, AlertTriangle, Lock, Copy } from 'lucide-react';
import { toast } from 'sonner';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';

const CONNECTOR_TYPES = ['Cam-Lock', 'Hubbell', 'Edison'];
const DISTRO_INPUT_TYPES = ['Camlock', 'Hubbell', 'Other'];
const DISTRO_PHASE_TYPES = ['Single', '3Ø', 'Other'];
const DISTRO_VOLTAGE_OPTIONS = ['120', '208', '240', '208/240', '480'];
const OUTPUT_CONNECTION_TYPES = ['L21-30', 'Socapex', 'Edison'];
const BREAKER_MAP = { 'L21-30': '30A', 'Socapex': '6x20A', 'Edison': '20A' };
const SOCA_PHASE_ROTATION = ['A', 'B', 'C', 'A', 'B', 'C'];
const DEFAULT_LOAD_EDISON = 5;
const DEPARTMENT_OPTIONS = [
  { value: 'audio', label: 'Audio' },
  { value: 'video', label: 'Video' },
  { value: 'lighting', label: 'Lighting' },
  { value: 'other', label: 'Other' },
];

const EditableElectricTab = ({ event, onUpdate }) => {
  const [electric, setElectric] = useState(event?.electric || { connections: [], distros: [] });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, index: null, connection: null });
  const [deleteDistroConfirm, setDeleteDistroConfirm] = useState({ open: false, distroIdx: null, outputIdx: null, type: null });
  const [expandedDistros, setExpandedDistros] = useState({});

  // Track pending changes and backup timer
  const pendingChanges = useRef({});
  const backupTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  // Only sync with parent on initial mount
  useEffect(() => {
    if (isInitialMount.current) {
      setElectric(event?.electric || { connections: [], distros: [] });
      isInitialMount.current = false;
    }
  }, [event?.electric]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (backupTimeoutRef.current) {
        clearTimeout(backupTimeoutRef.current);
      }
    };
  }, []);

  // Perform the actual save
  const doSave = useCallback(async () => {
    if (Object.keys(pendingChanges.current).length === 0) return;
    
    setSaving(true);
    const changesToSave = { ...pendingChanges.current };
    pendingChanges.current = {};
    
    try {
      await onUpdate({ electric: changesToSave });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save:', error);
      pendingChanges.current = { ...changesToSave, ...pendingChanges.current };
    } finally {
      setSaving(false);
    }
  }, [onUpdate]);

  // Queue a change
  const queueChange = useCallback((newElectric) => {
    pendingChanges.current = newElectric;
    
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    
    backupTimeoutRef.current = setTimeout(() => {
      doSave();
    }, 1000);
  }, [doSave]);

  // Save immediately (for blur events)
  const saveNow = useCallback(() => {
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    doSave();
  }, [doSave]);

  const handleFieldChange = (field, value) => {
    const newElectric = { ...electric, [field]: value };
    setElectric(newElectric);
    queueChange(newElectric);
  };

  // Power Connections
  const addConnection = () => {
    const newConnections = [...(electric.connections || []), { connector_type: '', amperage: '', voltage: '', phase: '', connection_location: '', for_departments: [] }];
    const newElectric = { ...electric, connections: newConnections };
    setElectric(newElectric);
    queueChange(newElectric);
  };

  const updateConnection = (index, field, value) => {
    const newConnections = [...(electric.connections || [])];
    newConnections[index] = { ...newConnections[index], [field]: value };
    const newElectric = { ...electric, connections: newConnections };
    setElectric(newElectric);
    queueChange(newElectric);
  };

  const toggleDepartment = (index, dept) => {
    const newConnections = [...(electric.connections || [])];
    const current = newConnections[index].for_departments || [];
    if (current.includes(dept)) {
      newConnections[index] = { ...newConnections[index], for_departments: current.filter(d => d !== dept) };
    } else {
      newConnections[index] = { ...newConnections[index], for_departments: [...current, dept] };
    }
    const newElectric = { ...electric, connections: newConnections };
    setElectric(newElectric);
    queueChange(newElectric);
    saveNow(); // Checkboxes should save immediately
  };

  const confirmDeleteConnection = (index) => {
    setDeleteConfirm({ open: true, index, connection: (electric.connections || [])[index] });
  };

  const removeConnection = () => {
    if (deleteConfirm.index === null) return;
    const newConnections = (electric.connections || []).filter((_, i) => i !== deleteConfirm.index);
    const newElectric = { ...electric, connections: newConnections };
    setElectric(newElectric);
    queueChange(newElectric);
    setDeleteConfirm({ open: false, index: null, connection: null });
    toast.success('Power connection removed');
    setTimeout(saveNow, 0);
  };

  // ==================== DISTRO MANAGEMENT ====================

  const addDistro = () => {
    const newDistro = {
      id: `distro-${Date.now()}`,
      distro_id: '',
      location: '',
      input_type: 'Camlock',
      amps: '',
      voltage: '208',
      phase: '3Ø',
      total_capacity: '',
      outputs: [],
    };
    const newDistros = [...(electric.distros || []), newDistro];
    const newElectric = { ...electric, distros: newDistros };
    setElectric(newElectric);
    queueChange(newElectric);
    setExpandedDistros(prev => ({ ...prev, [newDistro.id]: true }));
  };

  const updateDistro = (distroIdx, field, value) => {
    const newDistros = [...(electric.distros || [])];
    newDistros[distroIdx] = { ...newDistros[distroIdx], [field]: value };
    const newElectric = { ...electric, distros: newDistros };
    setElectric(newElectric);
    queueChange(newElectric);
  };

  const removeDistro = () => {
    if (deleteDistroConfirm.distroIdx === null) return;
    const newDistros = (electric.distros || []).filter((_, i) => i !== deleteDistroConfirm.distroIdx);
    const newElectric = { ...electric, distros: newDistros };
    setElectric(newElectric);
    queueChange(newElectric);
    setDeleteDistroConfirm({ open: false, distroIdx: null, outputIdx: null, type: null });
    toast.success('Distro removed');
    setTimeout(saveNow, 0);
  };

  const addOutput = (distroIdx) => {
    const distro = (electric.distros || [])[distroIdx];
    if (!distro) return;
    const nextChannel = String((distro.outputs || []).length + 1);
    const newOutput = {
      id: `out-${Date.now()}`,
      channel: nextChannel,
      connection_type: '',
      breaker: '',
      phase: '',
      destination: '',
      department: '',
      estimated_load: '',
      notes: '',
      locked: false,
    };
    const newDistros = [...(electric.distros || [])];
    newDistros[distroIdx] = { ...newDistros[distroIdx], outputs: [...(distro.outputs || []), newOutput] };
    const newElectric = { ...electric, distros: newDistros };
    setElectric(newElectric);
    queueChange(newElectric);
  };

  const updateOutput = (distroIdx, outputIdx, field, value) => {
    const newDistros = [...(electric.distros || [])];
    const distro = { ...newDistros[distroIdx] };
    const outputs = [...(distro.outputs || [])];
    const output = { ...outputs[outputIdx] };

    output[field] = value;

    // Auto-set breaker when connection type changes
    if (field === 'connection_type') {
      output.breaker = BREAKER_MAP[value] || '';
      // Auto-assign phase for Socapex channels
      if (value === 'Socapex') {
        // Expand to 6 sub-channels if not already
      }
    }

    outputs[outputIdx] = output;
    distro.outputs = outputs;
    newDistros[distroIdx] = distro;
    const newElectric = { ...electric, distros: newDistros };
    setElectric(newElectric);
    queueChange(newElectric);
  };

  const removeOutput = () => {
    if (deleteDistroConfirm.distroIdx === null || deleteDistroConfirm.outputIdx === null) return;
    const newDistros = [...(electric.distros || [])];
    const distro = { ...newDistros[deleteDistroConfirm.distroIdx] };
    distro.outputs = (distro.outputs || []).filter((_, i) => i !== deleteDistroConfirm.outputIdx);
    newDistros[deleteDistroConfirm.distroIdx] = distro;
    const newElectric = { ...electric, distros: newDistros };
    setElectric(newElectric);
    queueChange(newElectric);
    setDeleteDistroConfirm({ open: false, distroIdx: null, outputIdx: null, type: null });
    toast.success('Output removed');
    setTimeout(saveNow, 0);
  };

  const toggleDistroExpanded = (distroId) => {
    setExpandedDistros(prev => ({ ...prev, [distroId]: !prev[distroId] }));
  };

  // ==================== CALCULATIONS ====================

  const getPhaseForOutput = (output, channelIdx) => {
    if (output.connection_type === 'Socapex') {
      return SOCA_PHASE_ROTATION[channelIdx % 6] || 'A';
    }
    if (output.connection_type === 'L21-30') {
      return '3Ø'; // 3-phase, split evenly
    }
    return output.phase || 'A';
  };

  const calcDistroStats = (distro) => {
    const outputs = distro.outputs || [];
    const phases = { A: 0, B: 0, C: 0 };
    let totalLoad = 0;

    outputs.forEach((out, idx) => {
      const load = parseFloat(out.estimated_load) || (out.connection_type === 'Edison' ? DEFAULT_LOAD_EDISON : 0);
      totalLoad += load;

      if (out.connection_type === 'L21-30') {
        // 3-phase load split evenly
        phases.A += load / 3;
        phases.B += load / 3;
        phases.C += load / 3;
      } else if (out.connection_type === 'Socapex') {
        // Soca rotation: A/B/C/A/B/C per channel
        const chIdx = parseInt(out.channel) - 1;
        const phase = SOCA_PHASE_ROTATION[chIdx % 6] || 'A';
        phases[phase] = (phases[phase] || 0) + load;
      } else {
        const phase = out.phase || 'A';
        if (phases[phase] !== undefined) phases[phase] += load;
        else phases.A += load;
      }
    });

    const isSinglePhase = distro.phase === 'Single';
    const inputAmps = parseFloat(distro.amps) || 0;
    const maxPhase = Math.max(phases.A, phases.B, phases.C);
    const minPhase = Math.min(phases.A, phases.B, phases.C);
    const avgPhase = (phases.A + phases.B + phases.C) / 3;
    const imbalancePercent = !isSinglePhase && avgPhase > 0 ? ((maxPhase - minPhase) / avgPhase) * 100 : 0;
    const utilizationPercent = inputAmps > 0 ? (maxPhase / inputAmps) * 100 : 0;

    return {
      phases,
      totalLoad: Math.round(totalLoad * 10) / 10,
      maxPhase: Math.round(maxPhase * 10) / 10,
      inputAmps,
      isSinglePhase,
      utilizationPercent: Math.round(utilizationPercent),
      imbalancePercent: Math.round(imbalancePercent),
      isOverload: utilizationPercent >= 100,
      isWarning: utilizationPercent >= 80 && utilizationPercent < 100,
      isImbalanced: !isSinglePhase && imbalancePercent > 20,
    };
  };

  return (
    <Card className="border-border/50 bg-card/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Zap className="h-5 w-5 text-orange-400" />
            Electric
          </CardTitle>
          <div className="flex items-center h-6">
            {saving && (
              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </Badge>
            )}
            {saved && !saving && (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Saved
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Power Connections */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm text-muted-foreground uppercase tracking-wider">Available Electrical Service</Label>
            <Button type="button" variant="outline" size="sm" onClick={addConnection}>
              <Plus className="h-4 w-4 mr-1" /> Add Electrical Service
            </Button>
          </div>
          {(electric.connections || []).length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="w-[120px]">Connector</TableHead>
                    <TableHead className="w-[80px]">Amps</TableHead>
                    <TableHead className="w-[80px]">Voltage</TableHead>
                    <TableHead className="w-[80px]">Phase</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>For Departments</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(electric.connections || []).map((conn, index) => (
                    <TableRow key={index} className="border-border/50">
                      <TableCell>
                        <Select
                          value={conn.connector_type || 'none'}
                          onValueChange={(v) => updateConnection(index, 'connector_type', v === 'none' ? '' : v)}
                        >
                          <SelectTrigger className="bg-background/50 h-8">
                            <SelectValue placeholder="Type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Select</SelectItem>
                            {CONNECTOR_TYPES.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={conn.amperage || ''}
                          onChange={(e) => updateConnection(index, 'amperage', e.target.value)}
                          onBlur={saveNow}
                          placeholder="A"
                          className="bg-background/50 h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={conn.voltage || ''}
                          onChange={(e) => updateConnection(index, 'voltage', e.target.value)}
                          onBlur={saveNow}
                          placeholder="V"
                          className="bg-background/50 h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={conn.phase || ''}
                          onChange={(e) => updateConnection(index, 'phase', e.target.value)}
                          onBlur={saveNow}
                          placeholder="Phase"
                          className="bg-background/50 h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={conn.connection_location || ''}
                          onChange={(e) => updateConnection(index, 'connection_location', e.target.value)}
                          onBlur={saveNow}
                          placeholder="Location"
                          className="bg-background/50 h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {DEPARTMENT_OPTIONS.map((dept) => (
                            <label key={dept.value} className="flex items-center gap-1 cursor-pointer">
                              <Checkbox
                                checked={(conn.for_departments || []).includes(dept.value)}
                                onCheckedChange={() => toggleDepartment(index, dept.value)}
                              />
                              <span className="text-xs">{dept.label}</span>
                            </label>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => confirmDeleteConnection(index)} className="hover:bg-destructive/10">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4 text-sm">No electrical service listed. Click "Add Electrical Service" to add one.</p>
          )}
        </div>

        <Separator />

        {/* Department Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Audio Electric Notes</Label>
            <Textarea
              value={electric.audio_notes || ''}
              onChange={(e) => handleFieldChange('audio_notes', e.target.value)}
              onBlur={saveNow}
              placeholder="Audio power requirements..."
              rows={3}
              className="bg-background/50"
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Video Electric Notes</Label>
            <Textarea
              value={electric.video_notes || ''}
              onChange={(e) => handleFieldChange('video_notes', e.target.value)}
              onBlur={saveNow}
              placeholder="Video power requirements..."
              rows={3}
              className="bg-background/50"
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Lighting Electric Notes</Label>
            <Textarea
              value={electric.lighting_notes || ''}
              onChange={(e) => handleFieldChange('lighting_notes', e.target.value)}
              onBlur={saveNow}
              placeholder="Lighting power requirements..."
              rows={3}
              className="bg-background/50"
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Other Electric Notes</Label>
            <Textarea
              value={electric.other_notes || ''}
              onChange={(e) => handleFieldChange('other_notes', e.target.value)}
              onBlur={saveNow}
              placeholder="Other power requirements..."
              rows={3}
              className="bg-background/50"
            />
          </div>
        </div>

        <Separator />

        {/* Electrical Distribution */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm text-muted-foreground uppercase tracking-wider">Electrical Distribution</Label>
            <Button type="button" variant="outline" size="sm" onClick={addDistro}>
              <Plus className="h-4 w-4 mr-1" /> Add Distro
            </Button>
          </div>

          {(electric.distros || []).length === 0 ? (
            <p className="text-muted-foreground text-center py-4 text-sm">No distribution panels configured. Click "Add Distro" to add one.</p>
          ) : (
            <div className="space-y-4">
              {(electric.distros || []).map((distro, distroIdx) => {
                const stats = calcDistroStats(distro);
                const isExpanded = expandedDistros[distro.id] !== false;

                return (
                  <div key={distro.id || distroIdx} className="border border-border/50 rounded-lg overflow-hidden bg-background/30">
                    {/* Distro Header */}
                    <div
                      className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                      onClick={() => toggleDistroExpanded(distro.id)}
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      <Zap className="h-4 w-4 text-orange-400" />
                      <span className="font-medium text-sm">{distro.distro_id || 'Unnamed Distro'}</span>
                      {distro.location && <span className="text-xs text-muted-foreground">@ {distro.location}</span>}
                      <span className="text-xs text-muted-foreground ml-auto mr-2">
                        {distro.input_type} {distro.amps}A {distro.voltage}V {distro.phase} | {(distro.outputs || []).length} outputs
                      </span>
                      {stats.isOverload && <Badge variant="outline" className="border-red-500/30 bg-red-500/10 text-red-400 text-xs">OVERLOAD</Badge>}
                      {stats.isWarning && !stats.isOverload && <Badge variant="outline" className="border-yellow-500/30 bg-yellow-500/10 text-yellow-400 text-xs">WARNING</Badge>}
                      {stats.isImbalanced && <Badge variant="outline" className="border-orange-500/30 bg-orange-500/10 text-orange-400 text-xs">IMBALANCED</Badge>}
                    </div>

                    {isExpanded && (
                      <div className="border-t border-border/50 p-3 space-y-4">
                        {/* Distro Input Details */}
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
                          <div>
                            <Label className="text-xs text-muted-foreground">Distro ID</Label>
                            <Input value={distro.distro_id || ''} onChange={(e) => updateDistro(distroIdx, 'distro_id', e.target.value)} onBlur={saveNow} placeholder="D1" className="bg-background/50 h-8 text-xs" />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Location</Label>
                            <Input value={distro.location || ''} onChange={(e) => updateDistro(distroIdx, 'location', e.target.value)} onBlur={saveNow} placeholder="Stage Left" className="bg-background/50 h-8 text-xs" />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Input Type</Label>
                            <Select value={distro.input_type || 'Camlock'} onValueChange={(v) => { updateDistro(distroIdx, 'input_type', v); saveNow(); }}>
                              <SelectTrigger className="bg-background/50 h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {DISTRO_INPUT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Amps</Label>
                            <Input value={distro.amps || ''} onChange={(e) => updateDistro(distroIdx, 'amps', e.target.value)} onBlur={saveNow} placeholder="200" className="bg-background/50 h-8 text-xs" />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Voltage</Label>
                            <Select value={distro.voltage || '208'} onValueChange={(v) => { updateDistro(distroIdx, 'voltage', v); saveNow(); }}>
                              <SelectTrigger className="bg-background/50 h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {DISTRO_VOLTAGE_OPTIONS.map(v => <SelectItem key={v} value={v}>{v}V</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Phase</Label>
                            <Select value={distro.phase || '3Ø'} onValueChange={(v) => { updateDistro(distroIdx, 'phase', v); saveNow(); }}>
                              <SelectTrigger className="bg-background/50 h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {DISTRO_PHASE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Disconnect Location</Label>
                            <Input value={distro.disconnect_location || ''} onChange={(e) => updateDistro(distroIdx, 'disconnect_location', e.target.value)} onBlur={saveNow} placeholder="Panel 2A" className="bg-background/50 h-8 text-xs" />
                          </div>
                          <div className="flex items-end">
                            <Button variant="ghost" size="icon" onClick={() => setDeleteDistroConfirm({ open: true, distroIdx, outputIdx: null, type: 'distro' })} className="hover:bg-destructive/10 h-8 w-8">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>

                        {/* Phase Load Summary */}
                        {(distro.outputs || []).length > 0 && (() => {
                          const voltage = parseFloat(distro.voltage) || (distro.voltage === '208/240' ? 208 : 0);
                          const totalWatts = voltage > 0 ? Math.round(stats.totalLoad * voltage) : null;

                          return (
                          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 p-2 rounded-lg bg-muted/30 text-xs">
                            {!stats.isSinglePhase ? (<>
                            <div>
                              <span className="text-muted-foreground">Phase A:</span>{' '}
                              <span className="font-mono font-medium text-red-400">{Math.round(stats.phases.A * 10) / 10}A</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Phase B:</span>{' '}
                              <span className="font-mono font-medium text-blue-400">{Math.round(stats.phases.B * 10) / 10}A</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Phase C:</span>{' '}
                              <span className="font-mono font-medium text-emerald-400">{Math.round(stats.phases.C * 10) / 10}A</span>
                            </div>
                            </>) : (
                            <div>
                              <span className="text-muted-foreground">Load:</span>{' '}
                              <span className="font-mono font-medium">{stats.totalLoad}A</span>
                            </div>
                            )}
                            <div>
                              <span className="text-muted-foreground">Total:</span>{' '}
                              <span className="font-mono font-medium">{stats.totalLoad}A</span>
                              {totalWatts !== null && <span className="text-muted-foreground ml-1">({totalWatts >= 1000 ? `${(totalWatts / 1000).toFixed(1)}kW` : `${totalWatts}W`})</span>}
                            </div>
                            <div>
                              <span className="text-muted-foreground">Utilization:</span>{' '}
                              <span className={`font-mono font-medium ${stats.isOverload ? 'text-red-400' : stats.isWarning ? 'text-yellow-400' : 'text-emerald-400'}`}>
                                {stats.utilizationPercent}%
                              </span>
                              {stats.isImbalanced && <span className="text-orange-400 ml-1">(imbalanced {stats.imbalancePercent}%)</span>}
                            </div>
                          </div>
                          );
                        })()}

                        {/* Outputs Table */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Outputs</Label>
                            <Button type="button" variant="outline" size="sm" onClick={() => addOutput(distroIdx)} className="h-7 text-xs">
                              <Plus className="h-3 w-3 mr-1" /> Add Output
                            </Button>
                          </div>
                          {(distro.outputs || []).length > 0 ? (
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="text-xs w-[50px]">Ch #</TableHead>
                                    <TableHead className="text-xs w-[100px]">Connection</TableHead>
                                    <TableHead className="text-xs w-[70px]">Breaker</TableHead>
                                    <TableHead className="text-xs w-[50px]">Phase</TableHead>
                                    <TableHead className="text-xs">Destination</TableHead>
                                    <TableHead className="text-xs w-[80px]">Dept</TableHead>
                                    <TableHead className="text-xs w-[70px]">Load (A)</TableHead>
                                    <TableHead className="text-xs w-[65px]">Cable Len</TableHead>
                                    <TableHead className="text-xs">Notes</TableHead>
                                    <TableHead className="text-xs w-[40px]"></TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(distro.outputs || []).map((output, outIdx) => {
                                    const autoPhase = getPhaseForOutput(output, parseInt(output.channel) - 1 || outIdx);
                                    const load = parseFloat(output.estimated_load) || 0;
                                    const breakerAmps = output.connection_type === 'L21-30' ? 30 : output.connection_type === 'Edison' ? 20 : output.connection_type === 'Socapex' ? 20 : 0;
                                    const loadWarning = breakerAmps > 0 && load > 0 && (load / breakerAmps) >= 0.8;

                                    return (
                                      <TableRow key={output.id || outIdx} className={output.locked ? 'bg-amber-500/5' : ''}>
                                        <TableCell className="p-1">
                                          <Input value={output.channel || ''} onChange={(e) => updateOutput(distroIdx, outIdx, 'channel', e.target.value)} onBlur={saveNow} className="bg-background/50 h-7 text-xs w-full" />
                                        </TableCell>
                                        <TableCell className="p-1">
                                          <Select value={output.connection_type || 'none'} onValueChange={(v) => { updateOutput(distroIdx, outIdx, 'connection_type', v === 'none' ? '' : v); saveNow(); }}>
                                            <SelectTrigger className="bg-background/50 h-7 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="none">-</SelectItem>
                                              {OUTPUT_CONNECTION_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                            </SelectContent>
                                          </Select>
                                        </TableCell>
                                        <TableCell className="p-1 text-xs text-muted-foreground font-mono">{output.breaker || BREAKER_MAP[output.connection_type] || '-'}</TableCell>
                                        <TableCell className="p-1 text-xs font-mono">
                                          {output.connection_type === 'Socapex' || output.connection_type === 'L21-30' ? (
                                            <span className={autoPhase === '3Ø' ? 'text-amber-400' : autoPhase === 'A' ? 'text-red-400' : autoPhase === 'B' ? 'text-blue-400' : 'text-emerald-400'}>
                                              {autoPhase}
                                            </span>
                                          ) : (
                                            <Select value={output.phase || 'A'} onValueChange={(v) => { updateOutput(distroIdx, outIdx, 'phase', v); saveNow(); }}>
                                              <SelectTrigger className="bg-background/50 h-7 text-xs w-[52px]"><SelectValue /></SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="A"><span className="text-red-400 font-mono">A</span></SelectItem>
                                                <SelectItem value="B"><span className="text-blue-400 font-mono">B</span></SelectItem>
                                                <SelectItem value="C"><span className="text-emerald-400 font-mono">C</span></SelectItem>
                                              </SelectContent>
                                            </Select>
                                          )}
                                        </TableCell>
                                        <TableCell className="p-1">
                                          <Input value={output.destination || ''} onChange={(e) => updateOutput(distroIdx, outIdx, 'destination', e.target.value)} onBlur={saveNow} placeholder="FOH Audio" className="bg-background/50 h-7 text-xs" />
                                        </TableCell>
                                        <TableCell className="p-1">
                                          <Input value={output.department || ''} onChange={(e) => updateOutput(distroIdx, outIdx, 'department', e.target.value)} onBlur={saveNow} placeholder="Audio" className="bg-background/50 h-7 text-xs" />
                                        </TableCell>
                                        <TableCell className="p-1">
                                          <Input
                                            value={output.estimated_load || ''}
                                            onChange={(e) => updateOutput(distroIdx, outIdx, 'estimated_load', e.target.value)}
                                            onBlur={saveNow}
                                            placeholder={output.connection_type === 'Edison' ? `${DEFAULT_LOAD_EDISON}` : '0'}
                                            className={`bg-background/50 h-7 text-xs font-mono ${loadWarning ? 'border-yellow-500/50' : ''}`}
                                          />
                                        </TableCell>
                                        <TableCell className="p-1">
                                          <Input value={output.cable_length || ''} onChange={(e) => updateOutput(distroIdx, outIdx, 'cable_length', e.target.value)} onBlur={saveNow} placeholder="" className="bg-background/50 h-7 text-xs font-mono" />
                                        </TableCell>
                                        <TableCell className="p-1">
                                          <Input value={output.notes || ''} onChange={(e) => updateOutput(distroIdx, outIdx, 'notes', e.target.value)} onBlur={saveNow} className="bg-background/50 h-7 text-xs" />
                                        </TableCell>
                                        <TableCell className="p-1">
                                          <Button variant="ghost" size="icon" onClick={() => setDeleteDistroConfirm({ open: true, distroIdx, outputIdx: outIdx, type: 'output' })} className="hover:bg-destructive/10 h-7 w-7">
                                            <Trash2 className="h-3 w-3 text-destructive" />
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          ) : (
                            <p className="text-muted-foreground text-center py-3 text-xs">No outputs. Click "Add Output" to add circuits.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, index: null, connection: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Power Connection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this power connection?
              {deleteConfirm.connection && (
                <span className="block mt-2 font-medium text-foreground">
                  {deleteConfirm.connection.connector_type || 'Unknown type'} - {deleteConfirm.connection.amperage || '?'}A @ {deleteConfirm.connection.voltage || '?'}V
                </span>
              )}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={removeConnection}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Distro/Output Delete Confirmation */}
      <AlertDialog open={deleteDistroConfirm.open} onOpenChange={(open) => !open && setDeleteDistroConfirm({ open: false, distroIdx: null, outputIdx: null, type: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteDistroConfirm.type === 'distro' ? 'Distribution Panel' : 'Output Circuit'}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDistroConfirm.type === 'distro'
                ? 'Are you sure you want to delete this entire distribution panel and all its outputs? This action cannot be undone.'
                : 'Are you sure you want to delete this output circuit? This action cannot be undone.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteDistroConfirm.type === 'distro' ? removeDistro : removeOutput}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default EditableElectricTab;
